import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom';
import DriverLayout from '../../layouts/DriverLayout'
import DriverService from '../../services/DriverService';
import {
    Box, Button, Text, Avatar, Link, Stack, Flex, HStack, Heading,
    FormControl, FormLabel, Grid, Input, Select, IconButton, Tooltip, Divider, Tag,
    useToast
} from "@chakra-ui/react";
import { FaEdit, FaFileAlt, FaCogs, FaFileMedical } from "react-icons/fa";
import { getVehicleType, getStatus, formatDate } from '../../utils/helper';

import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as Yup from "yup";
import config from '../../constants/config';

const validationSchema = Yup.object().shape({
    VMake: Yup.string().required("Make is required"),
    VModel: Yup.string().required("Model is required"),
    VNo: Yup.string().required("Vehicle No is required"),
    VEngNo: Yup.string().required("Engine No is required"),
    VRegNo: Yup.string().required("Reg No is required"),
    VRegDt: Yup.string().required("Reg Date is required"),
    VRegEndDt: Yup.string().required("Reg End Date is required"),
    VInsNo: Yup.string().required("Insurance No is required"),
    VInsCompany: Yup.string().required("Insurance Company is required"),
    VInsEndDt: Yup.string().required("Insurance End Date is required"),
    VColor: Yup.string().required("Color is required"),
    VTyp: Yup.string().required("Type is required"),
    vehiclePic: Yup
        .mixed()
        .required("Vehicle Picture is required")
        .test("fileType", "Only images allowed", (value) =>
            value && ["image/jpeg", "image/png"].includes(value.type)
        ),
    registrationPic: Yup.mixed().required("Registration Pic is required"),
    enginePic: Yup.mixed().required("Engine Pic is required"),
    insurancePic: Yup.mixed().required("Insurance Pic is required"),
});

const VehicleList = () => {
    const user = JSON.parse(localStorage.getItem("user"));
    const toast = useToast();

    const {
        register,
        handleSubmit,
        reset,
        setValue,
        formState: { errors }
    } = useForm({
        resolver: yupResolver(validationSchema),
    });

    const backendUrl = config.apiBaseUrl;
    const navigate = useNavigate();

    const [data, setData] = useState([]);

    const handleVehicle = async (data) => {

        const formDt = new FormData();
        formDt.append("VColor", data.VColor);
        formDt.append("VEngNo", data.VEngNo);
        formDt.append("VInsCompany", data.VInsCompany);
        formDt.append("VInsEndDt", data.VInsEndDt);
        formDt.append("VInsNo", data.VInsNo);
        formDt.append("VMake", data.VMake);
        formDt.append("VModel", data.VModel);
        formDt.append("VNo", data.VNo);
        formDt.append("VRegDt", data.VRegDt);
        formDt.append("VRegEndDt", data.VRegEndDt);
        formDt.append("VRegNo", data.VRegNo);
        formDt.append("VTyp", data.VTyp);
        formDt.append("Stat", 1);
        formDt.append("uId", user.uId);
        formDt.append("DVNo", 0);
        formDt.append("vehiclePic", data.vehiclePic);
        formDt.append("registrationPic", data.registrationPic);
        formDt.append("enginePic", data.enginePic);
        formDt.append("insurancePic", data.insurancePic);

        try {
            const res = await DriverService.saveVehicle(formDt);
            if (res.statusCode === 200) {
                reset();
                toast({
                    title: 'Success',
                    description: res.data.message,
                    status: 'success',
                    duration: 3000,
                    isClosable: true,
                    position: 'top-right',
                });
                vehicleListing();
            }
        } catch (error) {
            if (error.response?.status === 401) {
                toast({
                    title: 'Error',
                    description: error.response.data.message,
                    status: 'error',
                    duration: 3000,
                    isClosable: true,
                    position: 'top-right',
                });
                // setotpErrors(error.response.data.message)
            } else {
                toast({
                    title: 'Error',
                    description: "Something went wrong. Please try again.",
                    status: 'error',
                    duration: 3000,
                    isClosable: true,
                    position: 'top-right',
                });
                console.error(error);
            }
        }
    }

    useEffect(() => {
        vehicleListing();
    }, []);

    const vehicleListing = async () => {
        setData([]);
        try {
            const res = await DriverService.getVehicleList(user.uId);
            setData(res.data);
        } catch (error) {
            toast({
                title: 'Error',
                description: "Error fetching vehicle",
                status: 'error',
                duration: 3000,
                isClosable: true,
                position: 'top-right',
            });
        }
    };

    const handleEdit = (DVNo) => {
        navigate(`/driver/vehicle_edit/${DVNo}`);
    };

    return (
        <DriverLayout>
            <Box maxW="1000px" mx="auto" px={4} >
                <Box maxW="900px" mx="auto" mt={2}>
                    <Box bg="blue.500" color="white" py={3} px={6} borderTopRadius="lg">
                        <Heading size="sm">Add Vehicle</Heading>
                    </Box>

                    {/* Form Card Body */}
                    <Box
                        bg="white"
                        p={6}
                        roundedBottom="xl"
                        boxShadow="md"
                        border="1px solid"
                        borderColor="gray.200"
                    >
                        <form onSubmit={handleSubmit(handleVehicle)}>
                            <Grid templateColumns={{ base: "1fr", md: "repeat(3, 1fr)" }} gap={3}>
                                <FormControl>
                                    <FormLabel fontSize="sm">Make (Company)</FormLabel>
                                    <Input type="text" {...register("VMake")} size="sm" />
                                    <Box color="red.500" fontSize="xs">{errors.VMake?.message}</Box>
                                </FormControl>

                                <FormControl>
                                    <FormLabel fontSize="sm">Model Name</FormLabel>
                                    <Input type="text" {...register("VModel")} size="sm" />
                                    <Box color="red.500" fontSize="xs">{errors.VModel?.message}</Box>
                                </FormControl>

                                <FormControl>
                                    <FormLabel fontSize="sm">Vehicle No</FormLabel>
                                    <Input type="text" {...register("VNo")} size="sm" />
                                    <Box color="red.500" fontSize="xs">{errors.VNo?.message}</Box>
                                </FormControl>

                                <FormControl>
                                    <FormLabel fontSize="sm">Engine No</FormLabel>
                                    <Input type="text" {...register("VEngNo")} size="sm" />
                                    <Box color="red.500" fontSize="xs">{errors.VEngNo?.message}</Box>
                                </FormControl>

                                <FormControl>
                                    <FormLabel fontSize="sm">Registration No</FormLabel>
                                    <Input type="text" {...register("VRegNo")} size="sm" />
                                    <Box color="red.500" fontSize="xs">{errors.VRegNo?.message}</Box>
                                </FormControl>

                                <FormControl>
                                    <FormLabel fontSize="sm">Registration Date</FormLabel>
                                    <Input type="date" {...register("VRegDt")} size="sm" />
                                    <Box color="red.500" fontSize="xs">{errors.VRegDt?.message}</Box>
                                </FormControl>

                                <FormControl>
                                    <FormLabel fontSize="sm">Registration End Date</FormLabel>
                                    <Input type="date" {...register("VRegEndDt")} size="sm" />
                                    <Box color="red.500" fontSize="xs">{errors.VRegEndDt?.message}</Box>
                                </FormControl>

                                <FormControl>
                                    <FormLabel fontSize="sm">Insurance No</FormLabel>
                                    <Input type="text" {...register("VInsNo")} size="sm" />
                                    <Box color="red.500" fontSize="xs">{errors.VInsNo?.message}</Box>
                                </FormControl>

                                <FormControl fontSize="sm">
                                    <FormLabel>Insurance Company</FormLabel>
                                    <Input type="text" {...register("VInsCompany")} size="sm" />
                                    <Box color="red.500" fontSize="xs">{errors.VInsCompany?.message}</Box>
                                </FormControl>

                                <FormControl fontSize="sm">
                                    <FormLabel>Insurance End Date</FormLabel>
                                    <Input type="date" {...register("VInsEndDt")} size="sm" />
                                    <Box color="red.500" fontSize="xs">{errors.VInsEndDt?.message}</Box>
                                </FormControl>

                                <FormControl fontSize="sm">
                                    <FormLabel>Color</FormLabel>
                                    <Input type="text" {...register("VColor")} size="sm" />
                                    <Box color="red.500" fontSize="xs">{errors.VColor?.message}</Box>
                                </FormControl>

                                <FormControl>
                                    <FormLabel fontSize="sm">Type</FormLabel>
                                    <Select {...register("VTyp")} size="sm">
                                        <option value="">Select</option>
                                        <option value="2">2-Wheeler</option>
                                        <option value="4">4-Wheeler</option>
                                        <option value="6">6-Passenger</option>
                                        <option value="7">7-Passenger</option>
                                    </Select>
                                    <Box color="red.500" fontSize="xs">{errors.VTyp?.message}</Box>
                                </FormControl>

                                <FormControl isInvalid={errors.vehiclePic}>
                                    <FormLabel fontSize="sm">Vehicle Pic</FormLabel>
                                    <Input
                                        type="file"
                                        onChange={(e) => setValue("vehiclePic", e.target.files[0])}
                                        size='sm'
                                    />
                                    {errors.vehiclePic && <Text color="red.500">{errors.vehiclePic.message}</Text>}
                                </FormControl>

                                <FormControl isInvalid={errors.registrationPic}>
                                    <FormLabel fontSize="sm">Registration Pic</FormLabel>
                                    <Input
                                        type="file"
                                        onChange={(e) => setValue("registrationPic", e.target.files[0])}
                                        size='sm'
                                    />
                                    {errors.registrationPic && <Text color="red.500">{errors.registrationPic.message}</Text>}
                                </FormControl>

                                <FormControl isInvalid={errors.enginePic}>
                                    <FormLabel fontSize="sm">Engine Pic</FormLabel>
                                    <Input
                                        type="file"
                                        onChange={(e) => setValue("enginePic", e.target.files[0])}
                                        size='sm'
                                    />
                                    {errors.enginePic && <Text color="red.500">{errors.enginePic.message}</Text>}
                                </FormControl>

                                <FormControl isInvalid={errors.insurancePic}>
                                    <FormLabel fontSize="sm">Insurance Pic</FormLabel>
                                    <Input
                                        type="file"
                                        onChange={(e) => setValue("insurancePic", e.target.files[0])}
                                        size='sm'
                                    />
                                    {errors.insurancePic && <Text color="red.500">{errors.insurancePic.message}</Text>}
                                </FormControl>

                            </Grid>

                            <Box textAlign="center" pt={4}>
                                <Button
                                    type="submit"
                                    size="sm"
                                    bg="#62a13b"
                                    color="white"
                                    _hover={{ bg: "#538d32" }}
                                    boxShadow="lg"
                                    px={10}
                                    py={2}
                                    borderRadius="full"
                                    fontWeight="bold"
                                >
                                    Submit
                                </Button>
                            </Box>
                        </form>
                    </Box>
                </Box>
                <Box maxW="900px" mx="auto" mt={4}>
                    <Stack spacing={4} mt={3}>
                        {data.map((row) => (
                            <Box
                                key={row.DVNo}
                                p={4}
                                borderWidth="1px"
                                borderRadius="lg"
                                bg="white"
                                boxShadow="lg"
                            >
                                {/* Header */}
                                <Flex align="center" mb={3}>
                                    <Avatar
                                        src={`${backendUrl}/vehicle/${row.vehiclePic}`}
                                        name={row.VMake}
                                        size="md"
                                        mr={3}
                                    />
                                    <Box>
                                        <Text fontWeight="bold" fontSize="lg">
                                            {row.VMake}
                                        </Text>
                                        <Text fontSize="sm" color="gray.500">
                                            {getVehicleType(row.VTyp)}
                                        </Text>
                                    </Box>
                                </Flex>

                                {/* Vehicle Info */}
                                <Flex justify="space-between" align="center" mb={2}>
                                    <Text><b>Model:</b> {row.VModel}</Text>
                                    <Text><b>No:</b> {row.VNo}</Text>
                                </Flex>
                                <Flex justify="space-between" align="center" mb={2}>
                                    <Text><b>Engine No:</b> {row.VEngNo}</Text>
                                    <Text><Tag colorScheme="yellow">{row.VColor}</Tag></Text>
                                </Flex>

                                <Divider my={3} />
                                {/* Engine & Registration Info */}
                                <Flex justify="space-between" align="center" mb={2}>
                                    <Text><b>Reg No:</b> {row.VRegNo}</Text>
                                    <Text><b>Reg Date:</b> {formatDate(row.VRegDt)}</Text>
                                </Flex>

                                <Divider my={3} />
                                {/* Insurance Info */}
                                <Flex justify="space-between" align="center" mb={2}>
                                    <Text><b>Company:</b> {row.VInsCompany}</Text>
                                    <Text><b>Insurance:</b> {row.VInsNo}</Text>
                                </Flex>
                                <Flex justify="space-between" align="center" mb={2}>
                                    <Text><b>Ins End:</b> {formatDate(row.VInsEndDt)}</Text>
                                    <Text><Tag colorScheme="green">{getStatus(row.Stat)}</Tag></Text>
                                </Flex>

                                <Divider my={3} />

                                {/* File Icons & Edit */}
                                <Box>
                                    <HStack spacing={4} justify="center">
                                        <Tooltip label="Registration" hasArrow>
                                            <Link
                                                href={`${backendUrl}/registraionV/${row.registrationPic}`}
                                                isExternal
                                            >
                                                <IconButton
                                                    icon={<FaFileAlt />}
                                                    aria-label="Registration"
                                                    isRound
                                                    size="sm"
                                                    bg="blue.600"
                                                    color="white"
                                                    _hover={{ bg: "blue.700" }}
                                                />
                                            </Link>
                                        </Tooltip>

                                        <Tooltip label="Engine" hasArrow>
                                            <Link href={`${backendUrl}/engine/${row.enginePic}`} isExternal>
                                                <IconButton
                                                    icon={<FaCogs />}
                                                    aria-label="Engine"
                                                    isRound
                                                    size="sm"
                                                    bg="cyan.500"
                                                    color="white"
                                                    _hover={{ bg: "cyan.600" }}
                                                />
                                            </Link>
                                        </Tooltip>

                                        <Tooltip label="Insurance" hasArrow>
                                            <Link
                                                href={`${backendUrl}/insurance/${row.insurancePic}`}
                                                isExternal
                                            >
                                                <IconButton
                                                    icon={<FaFileMedical />}
                                                    aria-label="Insurance"
                                                    isRound
                                                    size="sm"
                                                    bg="pink.400"
                                                    color="white"
                                                    _hover={{ bg: "pink.500" }}
                                                />
                                            </Link>
                                        </Tooltip>

                                        <Tooltip label="Edit" hasArrow>
                                            <IconButton
                                                icon={<FaEdit />}
                                                aria-label="Edit"
                                                isRound
                                                size="sm"
                                                bg="yellow.400"
                                                onClick={() => handleEdit(row.DVNo)}
                                                color="white"
                                                _hover={{ bg: "yellow.500" }}
                                            />
                                        </Tooltip>
                                    </HStack>
                                </Box>
                            </Box>
                        ))}
                    </Stack>
                </Box>
            </Box>
        </DriverLayout>
    )
}

export default VehicleList
